import * as DataDogLogs from '~/lib/datadog/datadog';

/**
 * Cache for section rerender results
 * Key: Normalized URL string (may exclude certain parameters), Value: { html: object, timestamp: number }
 */
const rerenderCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 20;

/**
 * Creates a normalized cache key by excluding specified parameters and lowercasing all params
 * @param {string} url - The full URL
 * @param {string[]} excludeParams - Array of query parameter names to exclude from cache key
 * @returns {string} Normalized cache key
 */
function createCacheKey(url, excludeParams = []) {
  try {
    const urlObj = new URL(url);
    const normalizedParams = new URLSearchParams();

    // Normalize all search params to lowercase (both keys and values)
    urlObj.searchParams.forEach((value, key) => {
      const lowerKey = key.toLowerCase();
      // Skip excluded parameters
      if (!excludeParams.map((p) => p.toLowerCase()).includes(lowerKey)) {
        normalizedParams.set(lowerKey, value.toLowerCase());
      }
    });

    // Replace search params with normalized version
    urlObj.search = normalizedParams.toString();
    return urlObj.toString();
  } catch {
    // If URL parsing fails, return original URL
    return url;
  }
}

/**
 * Gets cached rerender result if valid
 * @param {string} cacheKey - The cache key (URL)
 * @returns {object|null} Cached data or null
 */
function getCachedResult(cacheKey) {
  const cached = rerenderCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.html;
  }
  if (cached) {
    rerenderCache.delete(cacheKey); // Remove expired entry
  }
  return null;
}

/**
 * Stores result in cache with FIFO eviction (first-in, first-out)
 * When cache is full, the oldest entry is removed
 * @param {string} cacheKey - The cache key (URL)
 * @param {object} html - The response data
 */
function setCachedResult(cacheKey, html) {
  if (rerenderCache.size >= MAX_CACHE_SIZE) {
    const firstKey = rerenderCache.keys().next().value;
    rerenderCache.delete(firstKey);
  }
  rerenderCache.set(cacheKey, { html, timestamp: Date.now() });
}

/**
 * Global registry for section rerender callbacks
 * BaseSection instances can register themselves here to be notified of rerenders
 */
const sectionRerenderRegistry = new Map();

// Track latest rerender request to avoid out-of-order DOM updates
let latestRerenderRequestId = 0;
let activeFetchController = null;

/**
 * Registers a callback to be called when a section is rerendered
 * @param {string} sectionId - The section ID
 * @param {Function} callback - The callback function to call
 */
export function registerSectionRerenderCallback(sectionId, callback) {
  sectionRerenderRegistry.set(sectionId, callback);
}

/**
 * Rerenders sections using Shopify's Section Rendering API
 * @param {HTMLElement|Array<string>} target - Container element or array of section IDs
 * @param {string|Object} options - Section type or section callbacks object
 * @param {Function} onComplete - Callback function called after successful rerender
 * @param {Function} onError - Callback function called on error
 * @returns {Promise<void>}
 */
export async function rerenderSections(target, options, onComplete, onError) {
  let fetchController = null;
  try {
    // Assign a new request id and cancel any in-flight fetch
    const requestId = ++latestRerenderRequestId;
    if (activeFetchController) {
      DataDogLogs.info('[Allbirds utils] - Section Rerenderer - Request cancelled', {
        newRequestId: requestId,
        oldRequestId: latestRerenderRequestId - 1,
        reason: 'newer-request',
      });
      try {
        activeFetchController.abort();
      } catch {
        // no-op
      }
    }
    fetchController = new AbortController();
    activeFetchController = fetchController;

    const isMultiple = Array.isArray(target);
    const sectionIds = isMultiple ? target : [target.getAttribute('data-section-id')];

    if (!sectionIds.every((id) => id)) {
      throw new Error('Invalid section ID(s) provided');
    }

    // Fetch sections from Shopify API with current query parameters
    const sectionsParam = sectionIds.join(',');
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('sections', sectionsParam);

    // Create normalized cache key (excludes tab parameter for better hit rates)
    const cacheKey = createCacheKey(currentUrl.toString(), ['tab']);

    // Check cache first
    const cachedData = getCachedResult(cacheKey);
    let data;

    if (cachedData) {
      DataDogLogs.info('[Allbirds utils] - Section Rerenderer - Cache hit', {
        cacheKey,
        sectionIds,
      });
      data = cachedData;
    } else {
      // Make fetch request
      const response = await fetch(currentUrl.toString(), { signal: fetchController.signal });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // If a newer request started while awaiting, bail
      if (requestId !== latestRerenderRequestId) {
        if (activeFetchController === fetchController) {
          activeFetchController = null;
        }
        return;
      }

      data = await response.json();

      // Cache the result
      setCachedResult(cacheKey, data);
      DataDogLogs.info('[Allbirds utils] - Section Rerenderer - Cache miss', {
        cacheKey,
        sectionIds,
      });
    }

    // Process each section
    const rerenderPromises = sectionIds.map(async (sectionId) => {
      if (requestId !== latestRerenderRequestId) return;
      const sectionHtml = data[sectionId];
      if (!sectionHtml) {
        throw new Error(`Section HTML not found in response for section: ${sectionId}`);
      }

      // Find the section container
      const container = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (!container) {
        throw new Error(`Section container not found for section: ${sectionId}`);
      }

      // Update section content
      await updateSectionContent(container, sectionHtml, sectionId);

      // Call section-specific completion callback if provided
      if (requestId !== latestRerenderRequestId) return;
      if (isMultiple && options[sectionId]?.onComplete) {
        options[sectionId].onComplete(container);
      }
    });

    // Wait for all sections to be processed
    await Promise.all(rerenderPromises);

    if (requestId !== latestRerenderRequestId) {
      if (activeFetchController === fetchController) {
        activeFetchController = null;
      }
      return;
    }

    // Call the global completion callback if provided
    if (onComplete) {
      onComplete();
    }

    if (activeFetchController === fetchController) {
      activeFetchController = null;
    }
  } catch (error) {
    // Ignore deliberate cancellations
    if (error && (error.name === 'AbortError' || error.code === 20)) {
      if (activeFetchController === fetchController) {
        activeFetchController = null;
      }
      return;
    }
    DataDogLogs.error(
      '[Allbirds utils] - Section Rerenderer - Error rerendering sections',
      {},
      error,
    );

    if (onError) {
      onError(error);
    }
    // Cleanup controller reference on error if still pointing at this request
    if (activeFetchController === fetchController) {
      activeFetchController = null;
    }
  }
}

/**
 * Section Rerenderer Utility
 *
 * Provides functionality to rerender Shopify sections using the Section Rendering API.
 * Handles fetching, parsing, and updating section content with proper cleanup.
 */

/**
 * Rerenders sections by their section types, automatically resolving to section IDs
 * @param {Array<string>} sectionTypes - Array of section types to rerender
 * @param {Object} options - Section callbacks object (optional)
 * @param {Function} onComplete - Callback function called after successful rerender
 * @param {Function} onError - Callback function called on error
 * @returns {Promise<void>}
 */
export async function rerenderSectionsByType(sectionTypes, options = {}, onComplete, onError) {
  try {
    const mergedOptions = {
      preserveScroll: true,
      ...options,
    };

    // Preserve current scroll position if requested
    const currentScrollY = mergedOptions.preserveScroll ? window.scrollY : null;

    // Find all section IDs for the given section types
    const sectionIds = [];
    const sectionCallbacks = {};

    sectionTypes.forEach((sectionType) => {
      const sections = document.querySelectorAll(`[data-section-type="${sectionType}"]`);

      sections.forEach((section) => {
        const sectionId = section.getAttribute('data-section-id');

        if (sectionId) {
          sectionIds.push(sectionId);
          // If options are provided for this section type, use them
          if (mergedOptions[sectionType]) {
            sectionCallbacks[sectionId] = mergedOptions[sectionType];
          }
        }
      });
    });

    if (sectionIds.length === 0) {
      throw new Error(`No sections found for types: ${sectionTypes.join(', ')}`);
    }

    // Create wrapped callbacks that restore scroll position if requested
    const wrappedOnComplete = () => {
      // Restore scroll position if preservation was requested
      if (mergedOptions.preserveScroll && currentScrollY !== null) {
        window.scrollTo(0, currentScrollY);
      }
      if (onComplete) {
        onComplete();
      }
    };

    const wrappedOnError = (error) => {
      // Restore scroll position even on error if preservation was requested
      if (mergedOptions.preserveScroll && currentScrollY !== null) {
        window.scrollTo(0, currentScrollY);
      }
      if (onError) {
        onError(error);
      }
    };

    // Use rerenderSections with resolved section IDs
    await rerenderSections(
      sectionIds,
      sectionCallbacks,
      () => {
        // After rerendering, trigger reinitialization for all affected sections
        sectionIds.forEach((sectionId) => {
          const callback = sectionRerenderRegistry.get(sectionId);
          if (callback) {
            callback();
          }
        });

        // Reinitialize all sections of the affected types
        sectionTypes.forEach((sectionType) => {
          const sections = document.querySelectorAll(`[data-section-type="${sectionType}"]`);

          sections.forEach((section) => {
            const sectionId = section.getAttribute('data-section-id');

            if (sectionId) {
              // Try to find the section instance and reinitialize it
              if (window.BaseSection && typeof window.BaseSection.getInstance === 'function') {
                const instanceData = window.BaseSection.getInstance(sectionId);

                if (instanceData && instanceData.section) {
                  // Reset initialization flag and reinitialize
                  instanceData.initialized = false;
                  instanceData.section._initInstance(instanceData);
                }
              }
            }
          });
        });
        if (wrappedOnComplete) {
          wrappedOnComplete();
        }
      },
      wrappedOnError,
    );
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

/**
 * Unregisters a section rerender callback
 * @param {string} sectionId - The section ID
 */
export function unregisterSectionRerenderCallback(sectionId) {
  sectionRerenderRegistry.delete(sectionId);
}

/**
 * Updates a section's content with new HTML
 * @param {HTMLElement} container - The section container element
 * @param {string} sectionHtml - The new section HTML
 * @param {string} sectionId - The section ID
 * @returns {Promise<void>}
 */
async function updateSectionContent(container, sectionHtml, sectionId) {
  // Parse the new section HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sectionHtml;

  // Find the new section element
  const newSection = tempDiv.querySelector(`[data-section-id="${sectionId}"]`);
  if (!newSection) {
    throw new Error(`New section not found in response HTML for section: ${sectionId}`);
  }

  // Clean up existing Vue app if present
  const currentApp = container.__vue_app__;
  if (currentApp) {
    // Find the section instance and call onUnload if it exists
    const sectionId = container.getAttribute('data-section-id');

    // Try to find the section instance using BaseSection.getInstance
    if (window.BaseSection && typeof window.BaseSection.getInstance === 'function') {
      const instanceData = window.BaseSection.getInstance(sectionId);
      if (
        instanceData &&
        instanceData.section &&
        typeof instanceData.section.onUnload === 'function'
      ) {
        instanceData.section.onUnload(instanceData);
      }
    }

    currentApp.unmount();
    delete container.__vue_app__;
  }

  // Update container content
  container.innerHTML = newSection.innerHTML;

  // Update data attributes
  newSection
    .getAttributeNames()
    .filter((attr) => attr.startsWith('data-'))
    .forEach((attr) => {
      container.setAttribute(attr, newSection.getAttribute(attr));
    });
}
